import React, { useState } from "react";
import { Box, Stack, Typography, Drawer, IconButton, Divider, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ClientNotes from "./ClientNotes.jsx";
import useBoBNotes from "../hooks/useBoBNotes";
import ClientDocuments from "./ClientDocuments.jsx";
import useBoBDocuments from "../hooks/useBoBDocuments";

const P = {
	navy: "#1A3F7A",
	navyMid: "#2D5FA8",
	blue: "#3A7DC9",
	blueLight: "#5BB8F5",
	blueFaint: "#EBF5FF",
	success: "#19A874",
	successBg: "#ECFFF7",
	border: "#D6E6F7",
	textSub: "#6B7FA3",
	bg: "#F4F8FD",
};

function StatusChip({ status }) {
	const map = {
		Effectuated: { bg: P.successBg, color: P.success, dot: P.success },
		Cancelled: { bg: "#FFF0F0", color: "#C62828", dot: "#C62828" },
		Terminated: { bg: "#FFF0F0", color: "#C62828", dot: "#C62828" },
		PendingEffectuation: { bg: "#FFF8E1", color: "#F57F17", dot: "#F9A825" },
	};
	const cfg = map[status] ?? { bg: P.blueFaint, color: P.navy, dot: P.blue };
	const label = status === "PendingEffectuation" ? "Pending Effectuation" : status;
	return (
		<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, backgroundColor: cfg.bg, borderRadius: 99, px: 1.25, py: 0.4 }}>
			<Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
			<Typography sx={{ fontSize: 11, fontWeight: 700, color: cfg.color, lineHeight: 1 }}>{label}</Typography>
		</Box>
	);
}

// `count` renders a badge beside the title, visible while the section is collapsed; 0 hides it.
function Section({ icon: Icon, title, children, defaultOpen = true, count = 0 }) {
	const [open, setOpen] = useState(defaultOpen);
	return (
		<Box sx={{ mb: 0.5 }}>
			<Box
				onClick={() => setOpen((v) => !v)}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 2,
					py: 1.25,
					cursor: "pointer",
					userSelect: "none",
					backgroundColor: open ? P.blueFaint : "#fff",
					borderLeft: `3px solid ${open ? P.blue : "transparent"}`,
					transition: "all 0.18s ease",
					"&:hover": { backgroundColor: P.blueFaint },
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1.25}>
					<Box
						sx={{
							width: 30,
							height: 30,
							borderRadius: 1.5,
							backgroundColor: open ? P.blue : "#E8EEF7",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transition: "background 0.18s",
						}}
					>
						<Icon sx={{ fontSize: 16, color: open ? "#fff" : P.navy }} />
					</Box>
					<Typography sx={{ fontSize: 13, fontWeight: 700, color: P.navy, letterSpacing: 0.3 }}>{title}</Typography>
					{count > 0 && (
						<Box
							aria-label={`${count} ${title.toLowerCase()}`}
							sx={{
								minWidth: 20,
								height: 20,
								px: 0.75,
								borderRadius: 99,
								backgroundColor: P.blue,
								color: "#fff",
								fontSize: 11,
								fontWeight: 700,
								lineHeight: "20px",
								textAlign: "center",
							}}
						>
							{count > 99 ? "99+" : count}
						</Box>
					)}
				</Stack>
				<Typography
					sx={{
						fontSize: 16,
						color: P.textSub,
						lineHeight: 1,
						transition: "transform 0.18s",
						transform: open ? "rotate(180deg)" : "rotate(0deg)",
					}}
				>
					⌃
				</Typography>
			</Box>

			{open && <Box sx={{ px: 2, pt: 1.5, pb: 2, backgroundColor: "#fff", borderLeft: `3px solid ${P.blue}` }}>{children}</Box>}
		</Box>
	);
}

function Field({ label, value, icon: Icon, full = false }) {
	if (!value && value !== 0) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5, width: full ? "100%" : { xs: "100%", sm: "calc(50% - 6px)" } }}>
			{Icon && (
				<Box
					sx={{
						width: 26,
						height: 26,
						borderRadius: 1,
						backgroundColor: P.blueFaint,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
						mt: 0.1,
					}}
				>
					<Icon sx={{ fontSize: 13, color: P.blue }} />
				</Box>
			)}
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: 10, color: P.textSub, lineHeight: 1.2, mb: 0.25, textTransform: "uppercase", letterSpacing: 0.4 }}>
					{label}
				</Typography>
				<Typography sx={{ fontSize: 13, fontWeight: 600, color: P.navy, lineHeight: 1.4, wordBreak: "break-word" }}>{value}</Typography>
			</Box>
		</Box>
	);
}

function EmptyState({ message }) {
	return (
		<Box sx={{ py: 2, textAlign: "center" }}>
			<Typography sx={{ fontSize: 12, color: P.textSub, fontStyle: "italic" }}>{message}</Typography>
		</Box>
	);
}

export default function ClientDetailPanel({ open, onClose, row }) {
	// Loaded on row open, not on expanding the sections, so their headers can show a count.
	// The list is the client's across every cutoff; row.period is only where new ones are filed.
	const notesState = useBoBNotes(row?.anchor_key ?? null, row?.period ?? null);
	const documentsState = useBoBDocuments(row?.anchor_key ?? null, row?.period ?? null);

	if (!row) return null;

	const fullName = [row.first_name, row.last_name, row.suffix].filter(Boolean).join(" ");

	const fullAddress = [row.address, row.unit, row.city, row.state, row.zip_code].filter(Boolean).join(", ");
	const mailingAddress = [row.mailing_address, row.mailing_city, row.mailing_state, row.mailing_zip_code].filter(Boolean).join(", ");
	const hasMailing = !!mailingAddress;
	const sameAddress = fullAddress === mailingAddress;

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: { xs: "100vw", sm: 440, md: "30% !important" },
					backgroundColor: P.bg,
					boxShadow: "-8px 0 32px rgba(26,63,122,0.12)",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				},
			}}
		>
			{/* ── Header ── */}
			<Box
				sx={{
					background: `linear-gradient(135deg, ${P.navy} 0%, ${P.navyMid} 60%, ${P.blue} 100%)`,
					px: 2.5,
					pt: 2.5,
					pb: 2,
					flexShrink: 0,
					position: "relative",
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: -20,
						right: -20,
						width: 100,
						height: 100,
						borderRadius: "50%",
						backgroundColor: "rgba(255,255,255,0.05)",
						pointerEvents: "none",
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						bottom: -30,
						right: 40,
						width: 70,
						height: 70,
						borderRadius: "50%",
						backgroundColor: "rgba(91,184,245,0.1)",
						pointerEvents: "none",
					}}
				/>

				<Stack direction="row" alignItems="flex-start" justifyContent="space-between !important">
					<Box>
						<Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.55)", letterSpacing: 1.5, textTransform: "uppercase", mb: 0.5 }}>
							Client Details
						</Typography>
						<Typography sx={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1.2, mb: 0.75 }}>{fullName}</Typography>
						<Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.75}>
							<StatusChip status={row.policy_status} />
						</Stack>
					</Box>
					<IconButton
						onClick={onClose}
						size="small"
						sx={{ color: "rgba(255,255,255,0.7)", mt: 3, "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" } }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			</Box>

			{/* ── Scrollable body ── */}
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					pb: 4,
					"&::-webkit-scrollbar": { width: 5 },
					"&::-webkit-scrollbar-track": { background: "transparent" },
					"&::-webkit-scrollbar-thumb": { background: P.border, borderRadius: 99 },
				}}
			>
				{/* ── 1. Notes ── */}
				{/* Notes and Documents sit first but start collapsed; their header badges show the
				    counts, so they don't push the client details down the panel. Keyed by the live
				    row so notes, drafts and edit state reset when another client opens. */}
				<Section key={row.anchor_key} icon={StickyNote2OutlinedIcon} title="Notes" defaultOpen={false} count={notesState.notes.length}>
					<ClientNotes clientKey={row.anchor_key} {...notesState} />
				</Section>

				<Divider sx={{ borderColor: P.border, mx: 2 }} />

				{/* ── 2. Documents ── */}
				<Section
					key={`docs-${row.anchor_key}`}
					icon={FolderOutlinedIcon}
					title="Documents"
					defaultOpen={false}
					count={documentsState.documents.length}
				>
					<ClientDocuments clientKey={row.anchor_key} {...documentsState} />
				</Section>

				<Divider sx={{ borderColor: P.border, mx: 2 }} />

				{/* ── 3. Client Details ── */}
				<Section icon={PersonOutlineIcon} title="Client Details">
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
						<Field label="Email" value={row.email} icon={EmailOutlinedIcon} full />
						<Field label="Phone" value={row.phone} icon={PhoneOutlinedIcon} />
						<Field label="Applicants" value={row.applicant_count} icon={GroupsOutlinedIcon} />
						<Field label="HealthSherpa ID" value={row.healthsherpa_id} icon={BadgeOutlinedIcon} />
					</Box>
				</Section>

				<Divider sx={{ borderColor: P.border, mx: 2 }} />

				{/* ── 4. Address ── */}
				<Section icon={HomeOutlinedIcon} title="Address">
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
						<Field label="Street" value={row.address} full />
						<Field label="Unit" value={row.unit} />
						<Field label="City" value={row.city} />
						<Field label="State" value={row.state} />
						<Field label="ZIP" value={row.zip_code} />
						<Field label="County" value={row.county} />
					</Box>

					{hasMailing && !sameAddress && (
						<>
							<Typography sx={{ fontSize: 10, color: P.textSub, textTransform: "uppercase", letterSpacing: 0.5, mb: 1, mt: 0.5 }}>
								Mailing Address
							</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
								<Field label="Mailing Street" value={row.mailing_address} full />
								<Field label="City" value={row.mailing_city} />
								<Field label="State" value={row.mailing_state} />
								<Field label="ZIP" value={row.mailing_zip_code} />
							</Box>
						</>
					)}

					{hasMailing && sameAddress && (
						<Typography sx={{ fontSize: 11, color: P.textSub, fontStyle: "italic", mt: 0.5 }}>Mailing address same as above.</Typography>
					)}
				</Section>

				<Divider sx={{ borderColor: P.border, mx: 2 }} />

				{/* ── 5. Policies ── */}
				<Section icon={ArticleOutlinedIcon} title="Policies">
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
						<Field label="Policy Status" value={row.policy_status} />
						<Field label="Effective Date" value={row.effective_date} icon={CalendarTodayOutlinedIcon} />
					</Box>
				</Section>

				<Divider sx={{ borderColor: P.border, mx: 2 }} />

				{/* ── 6. Carrier Information ── */}
				<Section icon={LocalHospitalOutlinedIcon} title="Carrier Information">
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
						<Field label="Issuer" value={row.issuer} />
						<Field label="Carrier ID (Policy ID)" value={row.carrier_provided_identifier} icon={BadgeOutlinedIcon} />
					</Box>
				</Section>

				<Divider sx={{ borderColor: P.border, mx: 2 }} />

				{/* ── 7. Renewal Information ── */}
				<Section icon={AutorenewOutlinedIcon} title="Renewal Information">
					<EmptyState message="No renewal information available." />
				</Section>
			</Box>
		</Drawer>
	);
}
